import { Box, Divider, Stack, Tab, Tabs } from "@mui/material";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ReportField } from "../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch.ts";
import { selectTabularFields } from "../../../store/selectors/fieldsStateSelectors.ts";
import { selectEditDisabled, selectSelectedBuilderTab } from "../../../store/selectors/uiSettingsSelectors.ts";
import { fieldsStateActions } from "../../../store/thunks/fieldStatesThunks.ts";
import { createUiSettingsAction } from "../../../store/utils/storeUtils.ts";
import type { BuilderTab } from "../../../store/types/uiSettingsState.types.ts";
import { isIrr } from "../../../utilities/fieldsHelper.ts";
import type { TableComponentProps } from "../../viewer/common/Types.ts";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import MeasureFieldsContainer from "../common/fields/format/MeasureFieldsContainer.tsx";
import ConditionsDropFieldsContainer from "../pivot/table/conditions/ConditionsDropFieldsContainer.tsx";
import CashFlowMeasureDropContainer from "./CashFlowMeasureDropContainer.tsx";
import CashFlowSplitDropContainer from "./CashFlowSplitDropContainer.tsx";
import CashFlowSettings from "./CashFlowSettings.tsx";

const CashFlowBuilder = ({ ReportTypeSelector }: TableComponentProps) => {
  const dispatch = useAppDispatch();
  const tab = useSelector(selectSelectedBuilderTab);
  const readonly = useSelector(selectEditDisabled);
  const fields = useSelector(selectTabularFields);
  const handleMeasureChange = (measure: ReportField, changes: Partial<FieldConfiguration>) =>
    dispatch(fieldsStateActions.updateFieldConfig({ field: measure, changes }));
  return (
    <Stack sx={{ flex: 1, mt: 2, gap: 2 }}>
      <Box sx={{ px: 2.5 }}>
        {ReportTypeSelector}
        <Tabs
          value={tab}
          sx={{ borderBottom: "none" }}
          onChange={(_, next: BuilderTab) => dispatch(createUiSettingsAction("setTab", next))}
        >
          <Tab value="Fields" label="Fields" />
          <Tab value="Settings" label="Settings" />
          <Tab value="Format" label="Format" />
        </Tabs>
      </Box>
      <Divider sx={{ mt: -2 }} />
      {tab === "Fields" && (
        <ScrollableFlexContainer scrollContainerSx={{ px: 2.5 }}>
          <Stack spacing={3}>
            <ConditionsDropFieldsContainer disableEqualitySelection readonlyMode={readonly} />
            <CashFlowMeasureDropContainer readonly={readonly} />
            <CashFlowSplitDropContainer readonly={readonly} />
          </Stack>
        </ScrollableFlexContainer>
      )}
      {tab === "Settings" && (
        <Box sx={{ px: 2.5 }}>
          <CashFlowSettings readonly={readonly} />
        </Box>
      )}
      {tab === "Format" && (
        <ScrollableFlexContainer scrollContainerSx={{ px: 2.5, pt: 1 }}>
          <Stack spacing={1.5}>
            <CashFlowSettings format readonly={readonly} />
            <MeasureFieldsContainer
              measures={fields.filter(isIrr)}
              onChange={handleMeasureChange}
              readonlyMode={readonly}
            />
          </Stack>
        </ScrollableFlexContainer>
      )}
    </Stack>
  );
};

export default CashFlowBuilder;
